import { type ReactNode, useId, useState } from 'react';
import { cx } from '../cx';
import { MOTION, useMotion } from '../motion';

/**
 * Label + control + hint/error, wired together by ids so the control is always
 * labelled and the error is always announced. `children` receives the id it must
 * use, which is less convenient than magic and much harder to get wrong.
 */
export interface FormFieldProps {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly required?: boolean | undefined;
  /**
   * THE CONTROL IS A GROUP, SO THE LABEL NAMES NOTHING (wave 4, C3 round 2, finding 6).
   *
   * `htmlFor` is dropped and the row's label becomes plain text over the group. Six rows of
   * the workflow forms hold a GROUP rather than one control - a segmented control, a column
   * of checkboxes, a list, two fields side by side, a whole value builder - and every one of
   * them pointed its `for` at an id no element on the page carried, which is a label a
   * screen reader drops on the floor. Each control in such a group carries its own
   * `ariaLabel`; this prop is how the row says so out loud rather than by accident.
   */
  readonly group?: boolean | undefined;
  readonly className?: string | undefined;
  readonly children: (props: {
    readonly id: string;
    readonly 'aria-describedby': string | undefined;
    readonly 'aria-invalid': true | undefined;
  }) => ReactNode;
}

export function FormField({
  label,
  hint,
  error,
  required = false,
  group = false,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const hintId = hint !== undefined ? `${id}-hint` : undefined;
  const errorId = error !== undefined ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter((value) => value !== undefined).join(' ');
  /**
   * THE FIELD'S ERROR IS ON THE FADE FAMILY (wave 5 round 4, M3; `docs/decisions/motion.md`
   * section 2, Fade). The R4 brief gives the auth forms "Fade on error text", and the line
   * beside the field is drawn HERE rather than on those screens - the sign-in screen cannot
   * reach it from its own file - so the family goes on the primitive and every form in the
   * product gets the same two halves of it.
   *
   * WHAT THE WIRING HAS TO KEEP APART is what the READER sees from what a SCREEN READER is
   * told. `aria-describedby` and `aria-invalid` follow the LIVE prop, so a field stops being
   * invalid on the commit its error clears; the paragraph follows `useMotion`, so it is still
   * in the tree for the 250 ms it takes to leave, holding the last sentence it was given.
   * A departing error is not a field that is still wrong.
   *
   * AND THE HINT WAITS FOR IT. The two lines are siblings in the same column, so a hint that
   * appeared while the error was still fading would grow the field by one line and shrink it
   * again - a wobble nobody asked for, in place of the one line that was always there.
   */
  const [shownError, setShownError] = useState(error);
  if (error !== undefined && error !== shownError) setShownError(error);
  const errorMotion = useMotion(error !== undefined);

  return (
    <div className={cx('space-y-1.5', className)}>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: a `group` row has no single control to
          point at - each control in the group carries its own `ariaLabel` - and the element
          stays a <label> because the row form's two-column grid selects `div:has(> label)`. */}
      <label
        {...(group ? {} : { htmlFor: id })}
        className="block text-xs font-medium text-ink-muted"
      >
        {label}
        {required && (
          <span className="text-danger" aria-hidden="true">
            {' *'}
          </span>
        )}
      </label>
      {children({
        id,
        'aria-describedby': describedBy.length > 0 ? describedBy : undefined,
        'aria-invalid': error !== undefined ? true : undefined,
      })}
      {errorMotion.mounted && (
        <p
          id={errorId}
          ref={errorMotion.ref}
          data-state={errorMotion.state}
          className={cx('text-xs text-danger', MOTION.fade)}
        >
          {shownError}
        </p>
      )}
      {hint !== undefined && error === undefined && !errorMotion.mounted && (
        <p id={hintId} className="text-xs text-ink-subtle">
          {hint}
        </p>
      )}
    </div>
  );
}
